import { groupByFunc } from "extensions/groupBy";
const deviceIdFieldSeperator = "$";
const fieldNameSeperator = "_";

const convertAbbreviationToUnit = (unitAbbreviation: string) => {
  switch (unitAbbreviation) {
    case "a":
      return "amps";
    case "v":
      return "volts";
    case "s":
      return "seconds";
    case "c":
      return "degrees celsius";
    case "pct":
      return "percent";
    case "hz":
      return "Hz";
    case "ah":
      return "Ah";
    case "kw":
      return "kW";
    case "kvar":
      return "kvar";
    case "kwh":
      return "kWh";
    case "kva":
      return "kVA";
    case "wh":
      return "Wh";
    case "mohm":
      return "mOhm";
    default:
      return undefined; // if we don't know it, assume field is unit-less
  }
};

/* groups cell_temperature_max_c and cell_temperature_min_c together
   so we can display them in the same highcharts, as they have the same unit
   and are conceptually linked */
export const groupFieldNamesByCommonFeature = (dataFieldNames: string[]) =>
  groupByFunc(dataFieldNames, (x) =>
    x.split(fieldNameSeperator).slice(0, 2).join(fieldNameSeperator)
  );

interface InferredColumnDefinition {
  columnName: string;
  dataField: string;
  dataFieldWithoutUnit: string;
  unit: string | undefined;
  deviceId: string | undefined;
  isUnitPercentage: boolean;
}

const hasDeviceIdFieldSeperator = (string: string) =>
  string.lastIndexOf(deviceIdFieldSeperator) > 0;

export const parseColumnName = (
  columnName: string
): InferredColumnDefinition => {
  const hasDeviceId = hasDeviceIdFieldSeperator(columnName);
  const deviceIdIndex = columnName.lastIndexOf(deviceIdFieldSeperator);
  const deviceId = hasDeviceId
    ? columnName.substring(deviceIdIndex + 1)
    : undefined;
  const columnNameWithoutDeviceId = hasDeviceId
    ? columnName.substring(0, deviceIdIndex)
    : columnName;
  const {
    isUnitPercentage,
    unit,
    columnNameWithoutDeviceIdOrUnit,
  } = tryToExtractUnitFromColumnNameWithoutDeviceId(columnNameWithoutDeviceId);

  return {
    columnName,
    dataField: columnNameWithoutDeviceId,
    dataFieldWithoutUnit: columnNameWithoutDeviceIdOrUnit,
    unit: unit,
    isUnitPercentage: isUnitPercentage,
    deviceId: deviceId,
  };
};

const tryToExtractUnitFromColumnNameWithoutDeviceId = (
  columnNameWithoutDeviceId: string
) => {
  const lastUnderscoreIndexWhichMightBeUnit = columnNameWithoutDeviceId.lastIndexOf(
    fieldNameSeperator
  );
  const unitAbbreviation =
    lastUnderscoreIndexWhichMightBeUnit > 0
      ? columnNameWithoutDeviceId.substring(
          lastUnderscoreIndexWhichMightBeUnit + 1
        )
      : undefined;
  const unit = unitAbbreviation
    ? convertAbbreviationToUnit(unitAbbreviation)
    : undefined;

  const columnNameWithoutDeviceIdOrUnit = unit
    ? columnNameWithoutDeviceId.substring(
        0,
        lastUnderscoreIndexWhichMightBeUnit
      )
    : columnNameWithoutDeviceId;
  return {
    columnNameWithoutDeviceIdOrUnit,
    unit,
    isUnitPercentage: unitAbbreviation === "pct",
  };
};

const longestCommonPrefix = (strings: string[]) => {
  if (!strings || strings.length === 0) return "";
  if (strings.length === 1) return strings[0];
  const smallest = strings.reduce(
    (min, str) => (min < str ? min : str),
    strings[0]
  );
  const largest = strings.reduce(
    (min, str) => (min > str ? min : str),
    strings[0]
  );
  for (let i = 0; i < smallest.length; i++) {
    if (smallest[i] !== largest[i]) return smallest.substr(0, i);
  }
  return smallest;
};

export const longestCommonPrefixToLastSeperator = (
  columnDefinitions: InferredColumnDefinition[]
) => {
  if (columnDefinitions.length === 0) return "";
  if (
    columnDefinitions.every(
      (x) => x.dataField === columnDefinitions[0].dataField
    )
  )
    return columnDefinitions[0].dataField;

  const commonDataFieldPrefix = longestCommonPrefix(
    columnDefinitions.map((x) => x.dataField)
  );
  const commonPrefixToLastSeperator = commonDataFieldPrefix.substring(
    0,
    commonDataFieldPrefix.lastIndexOf(fieldNameSeperator)
  );
  if (commonPrefixToLastSeperator.indexOf(fieldNameSeperator) > 0)
    return commonPrefixToLastSeperator;
  return "";
};

export const deriveTitleAndUnitsFromColumnNames = (columnNames: string[]) => {
  const columnDefinitions = columnNames.map(parseColumnName);

  const commonDataFieldPrefix = parseColumnName(
    longestCommonPrefixToLastSeperator(columnDefinitions)
  );

  const removePrefix = (string: string, prefix: string) => {
    if (prefix.length > 0) return string.substring(prefix.length + 1); // including 'seperator' character _ / $
    return string;
  };

  return {
    title: toHumanReadableTitleFromColumnDefinition(commonDataFieldPrefix),
    columnDefinitions: columnDefinitions.map((column) => ({
      columnName: column.columnName,
      unit: column.unit,
      isUnitPercentage: column.isUnitPercentage,
      title: toHumanReadableTitleFromColumnDefinition({
        ...column,
        dataFieldWithoutUnit: removePrefix(
          column.dataFieldWithoutUnit,
          commonDataFieldPrefix.dataFieldWithoutUnit
        ),
        dataField: removePrefix(
          column.dataField,
          commonDataFieldPrefix.dataField
        ),
      }),
    })),
  };
};

export const toHumanReadableTitle = (dataField: string) => {
  if (!dataField) return dataField;
  return toHumanReadableTitleFromColumnDefinition(parseColumnName(dataField));
};

const toHumanReadableTitleFromColumnDefinition = ({
  dataFieldWithoutUnit,
  deviceId,
}: InferredColumnDefinition) => {
  const nameSpaceified = dataFieldWithoutUnit.replace(/_/gi, " ");
  const titleCaseName = nameSpaceified
    ? `${nameSpaceified[0].toUpperCase()}${nameSpaceified.slice(1)}`
    : "";
  return `${deviceId ? deviceId + " " : ""}${titleCaseName}`.trim();
};
